/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Table, Spin, Select, Typography, Row, Button,
} from 'antd';
import moment from 'moment';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfigHeading from '../../configure/components/ConfigHeading';
import { selectLoadCurrencyReportsRequest } from '../../../../state/ui/reports/financial/financialUiReducer';
import { selectCurrencyReports } from '../../../../state/domain/financial/financialReducer';
import { requestLoadCurrencyReports } from '../../../../state/ui/reports/financial/financialUiActions';
import { asViaUrl, createPathWithParams, SESSION_ID_PARAM_KEY } from '../../../../utility/via-fetch';
import { getSessionCookie } from '../../../../utility/cookie-utility';

const { Column } = Table;
const { Option } = Select;

function ReportTable({
  isLoading, reports,
}) {
  const [visibleReports, setVisibleReports] = useState([]);
  useEffect(() => {
    const tempReports = reports.slice();
    setVisibleReports(tempReports.map(report => ({
      ...report,
      gross: report.gross.toFixed(2),
      net: report.net.toFixed(2),
      vat: report.vat.toFixed(2),
      commision: report.commision.toFixed(2),
      revenueRecognised: report.revenueRecognised ? report.revenueRecognised.toFixed(2) : 0,
      deferredIncome: report.deferredIncome ? report.deferredIncome.toFixed(2) : 0,
    })));
  }, [reports, setVisibleReports]);

  // TABLE SETTINGS, EXPANDED ROW, COLUMNS (key has region due to brief sharing of data with other reports)
  return (
    <Table
      pagination={false}
      loading={isLoading}
      dataSource={visibleReports}
      rowKey={report => report.currency + report.region}
    >
      <Column
        title={<FormattedMessage id="currency" />}
        dataIndex="currency"
        key="currency"
        align="center"
      />
      <Column
        title={<FormattedMessage id="grossEarnings" />}
        dataIndex="gross"
        key="gross"
        align="right"
      />
      <Column
        title={<FormattedMessage id="netEarnings" />}
        dataIndex="net"
        key="net"
        align="right"
      />
      <Column
        title={<FormattedMessage id="vatDue" />}
        dataIndex="vat"
        key="vat"
        align="right"
      />
      <Column
        title={<FormattedMessage id="commissionDeducted" />}
        dataIndex="commision"
        key="commision"
        align="right"
      />
    </Table>
  );
}

ReportTable.propTypes = {
  isLoading: PropTypes.bool,
  reports: PropTypes.arrayOf(PropTypes.shape({
  })),
};

ReportTable.defaultProps = {
  isLoading: false,
  reports: [],
};

function StateSelector({ setSelectedMonth }) {
  const intl = useIntl();
  const monthOptions = useMemo(() => {
    const monthList = [];
    for (let i = 1; i <= 12; i += 1) {
      // The 0 indexed month
      const monthIndex = moment.utc().subtract(i, 'M').month();
      const year = moment.utc().subtract(i, 'M').year();
      monthList.push({ value: monthIndex, text: `${intl.formatMessage({ id: `month${monthIndex}` })} ${year}` });
    }
    return monthList;
  }, [intl]);

  return (
    <span>
      <FormattedMessage id="month" />
      :
      <Select size="default" defaultValue={moment.utc().subtract(1, 'M').month()} style={{ width: '200px', margin: '10px' }} onChange={value => setSelectedMonth(value)}>
        { monthOptions ? monthOptions.map(option => (
          <Option key={option.value} value={option.value}>
            <Row type="flex" align="middle">
              <Typography.Text>
                {option.text}
              </Typography.Text>
            </Row>
          </Option>
        )) : <Option key="spin" value="spin"><Spin /></Option>}
      </Select>
    </span>
  );
}

StateSelector.propTypes = {
  setSelectedMonth: PropTypes.func.isRequired,
};

function SalesReport({
  isLoading, loadPageData, reports,
}) {
  const currentMonth = moment.utc().month();
  const lastMonth = (currentMonth - 1) % 12;
  const [selectedMonth, setSelectedMonth] = useState(lastMonth);

  const fromDate = moment.utc().month(selectedMonth).startOf('M');
  const toDate = moment.utc().month(selectedMonth).endOf('M');
  // This query does usages >= fromDate and <= toDate so it works from month start to month end.
  const fromDateString = currentMonth > selectedMonth ? fromDate.toISOString() : fromDate.subtract(1, 'y').toISOString();
  const toDateString = currentMonth > selectedMonth ? toDate.toISOString() : toDate.subtract(1, 'y').toISOString();

  useEffect(() => {
    loadPageData(
      fromDateString,
      toDateString,
    );
  }, [selectedMonth, loadPageData, fromDateString, toDateString]);
  return (
    <ConfigHeading
      titleId="salesReport"
    >
      <StateSelector setSelectedMonth={setSelectedMonth} lastMonth={lastMonth} />
      <a style={{ margin: '10px' }} href={createPathWithParams(asViaUrl('/api/tokens/purchase/monthly-report/download/SALES_VALUES'), { fromDate: fromDateString, toDate: toDateString, [SESSION_ID_PARAM_KEY]: getSessionCookie() })}><Button icon="download" type="primary"><FormattedMessage id="downloadReport" /></Button></a>
      <ReportTable
        isLoading={isLoading}
        reports={reports}
      />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
SalesReport.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  reports: PropTypes.arrayOf(PropTypes.shape({
  })),
};

SalesReport.defaultProps = {
  isLoading: false,
  reports: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectLoadCurrencyReportsRequest(state);
  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    reports: selectCurrencyReports(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: (fromDate, toDate) => dispatch(requestLoadCurrencyReports(fromDate, toDate)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(SalesReport);
